import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, Crown, TrendingUp } from 'lucide-react';

const UsageWarning = ({ resourceType = 'contacts' }) => {
  const { authFetch, user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsageStats();
  }, []);

  const fetchUsageStats = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/usage`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Treat owner as unlimited regardless of backend plan
  const isOwner = (user?.email || '').toLowerCase() === 'leslie@iabadvisors.com';
  if (isOwner && !loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-purple-600" />
          <div>
            <p className="text-purple-800 font-medium">
              Owner Plan - Unlimited {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
            </p>
            {usage?.usage?.[resourceType]?.current !== undefined && (
              <p className="text-purple-600 text-sm">
                Current usage: {usage.usage[resourceType].current.toLocaleString()} {resourceType}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading || !usage) return null;

  const resourceUsage = usage.usage[resourceType];
  const plan = usage.plan;

  // Don't show warning for unlimited plans
  if (resourceUsage.limit === -1) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-purple-600" />
          <div>
            <p className="text-purple-800 font-medium">
              {plan.name} Plan - Unlimited {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
            </p>
            <p className="text-purple-600 text-sm">
              Current usage: {resourceUsage.current.toLocaleString()} {resourceType}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show warning if near limit (80% or above)
  if (resourceUsage.near_limit || resourceUsage.percentage >= 80) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <div className="flex-1">
            <p className="text-yellow-800 font-medium">
              Approaching {resourceType} limit
            </p>
            <p className="text-yellow-700 text-sm">
              You've used {resourceUsage.current} of {resourceUsage.limit} {resourceType} ({Math.round(resourceUsage.percentage)}%)
            </p>
            <button 
              onClick={() => window.location.href = '/billing'}
              className="text-yellow-800 hover:text-yellow-900 text-sm font-medium underline mt-1 flex items-center gap-1"
            >
              <TrendingUp className="w-3 h-3" />
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default UsageWarning;